import {
  createSignal,
  on,
  onCleanup,
  createEffect,
  onMount,
  Show,
  batch,
  For,
  Switch,
  Match,
  createMemo,
  createResource,
  createComputed,
  children,
  untrack,
  createReaction,
} from "https://cdn.skypack.dev/solid-js@v1.7.2";
import { render } from "https://cdn.skypack.dev/solid-js@v1.7.2/web";
import { createStore } from "https://cdn.skypack.dev/solid-js@v1.7.2/store";
import html from "https://cdn.skypack.dev/solid-js@1.7.2/html";
import { animate, inView, spring } from "https://cdn.skypack.dev/motion";
import KeenSlider from "https://cdn.skypack.dev/keen-slider";

const formatResource = (id, { resourceType = "ProductVariant" }) =>
  `gid://shopify/${resourceType}/${id}`;

const fadeInScaleDefaultParams = {
  opacity: {
    exit: 0,
    enter: 1,
  },
  scale: {
    exit: 0.9,
    enter: 1,
  },
  y: {
    exit: "100px",
    enter: "0px",
  },
};

const animations = {
  slideFromBottom: (
    { enter = "0%", exit = "100%" } = { enter: "0%", exit: "100%" }
  ) => {
    const transition = { damping: 15 };
    return {
      name: `slideFromBottom`,
      onBeforeEnter: (el) => {
        el.style.transform = `translateY(${exit})`;
        el.style.opacity = 0;
      },
      onEnter: (el, done) =>
        animate(
          el,
          { transform: [null, `translateY(${enter})`], opacity: [null, 1] },
          { easing: spring(transition) }
        ).finished.then(done),
      onExit: (el, done) =>
        animate(
          el,
          { transform: [null, `translateY(${exit})`], opacity: [null, 0] },
          { easing: spring(transition) }
        ).finished.then(done),
    };
  },
  fadeInScale: ({
    opacity = fadeInScaleDefaultParams.opacity,
    scale = fadeInScaleDefaultParams.scale,
    y = fadeInScaleDefaultParams.y,
  } = fadeInScaleDefaultParams) => {
    const transition = { easing: "ease" };
    return {
      name: `fade`,
      onBeforeEnter: (el) => {
        el.style.opacity = opacity.exit;
        el.style.transform = `translate(var(--translate-x), ${y.exit}) scale(${scale.exit})`;
      },
      onEnter: (el, done) =>
        animate(
          el,
          {
            opacity: [null, opacity.enter],
            transform: [
              null,
              `translate(var(--translate-x), ${y.enter}) scale(${scale.enter})`,
            ],
          },
          transition
        ).finished.then(done),
      onExit: (el, done) =>
        animate(
          el,
          {
            opacity: [null, opacity.exit],
            transform: [
              null,
              `translate(var(--translate-x), ${y.exit}) scale(${scale.exit})`,
            ],
          },
          transition
        ).finished.then(done),
    };
  },
};

const parseResourceId = (id) => {
  const idArray = id.split("/");
  const actualId = idArray[idArray.length - 1];
  return actualId;
};

function nextFrame(fn) {
  requestAnimationFrame(() => {
    requestAnimationFrame(fn);
  });
}

const Transition = (props) => {
  let el;
  let first = true;
  const [s1, set1] = createSignal();
  const [s2, set2] = createSignal();
  const resolved = children(() => props.children);

  const {
    onBeforeEnter,
    onEnter,
    onAfterEnter,
    onBeforeExit,
    onExit,
    onAfterExit,
  } = props;

  const classnames = createMemo(() => {
    const name = props.name || "s";
    return {
      enterActiveClass: props.enterActiveClass || name + "-enter-active",
      enterClass: props.enterClass || name + "-enter",
      enterToClass: props.enterToClass || name + "-enter-to",
      exitActiveClass: props.exitActiveClass || name + "-exit-active",
      exitClass: props.exitClass || name + "-exit",
      exitToClass: props.exitToClass || name + "-exit-to",
    };
  });

  function enterTransition(el, prev) {
    if (!first || props.appear) {
      const enterClasses = classnames().enterClass.split(" ");
      const enterActiveClasses = classnames().enterActiveClass.split(" ");
      const enterToClasses = classnames().enterToClass.split(" ");
      onBeforeEnter && onBeforeEnter(el);
      el.classList.add(...enterClasses);
      el.classList.add(...enterActiveClasses);
      nextFrame(() => {
        el.classList.remove(...enterClasses);
        el.classList.add(...enterToClasses);
        onEnter && onEnter(el, () => endTransition());
        if (!onEnter || onEnter.length < 2) {
          el.addEventListener("transitionend", endTransition);
          el.addEventListener("animationend", endTransition);
        }
      });

      function endTransition(e) {
        if (el && (!e || e.target === el)) {
          el.removeEventListener("transitionend", endTransition);
          el.removeEventListener("animationend", endTransition);
          el.classList.remove(...enterActiveClasses);
          el.classList.remove(...enterToClasses);
          batch(() => {
            s1() !== el && set1(el);
            s2() === el && set2(undefined);
          });
          onAfterEnter && onAfterEnter(el);
          if (props.mode === "inout") exitTransition(el, prev);
        }
      }
    }
    prev && !props.mode ? set2(el) : set1(el);
  }

  function exitTransition(el, prev) {
    const exitClasses = classnames().exitClass.split(" ");
    const exitActiveClasses = classnames().exitActiveClass.split(" ");
    const exitToClasses = classnames().exitToClass.split(" ");
    if (!prev.parentNode) return endTransition();
    onBeforeExit && onBeforeExit(prev);
    prev.classList.add(...exitClasses);
    prev.classList.add(...exitActiveClasses);
    nextFrame(() => {
      prev.classList.remove(...exitClasses);
      prev.classList.add(...exitToClasses);
    });
    onExit && onExit(prev, () => endTransition());
    if (!onExit || onExit.length < 2) {
      prev.addEventListener("transitionend", endTransition);
      prev.addEventListener("animationend", endTransition);
    }

    function endTransition(e) {
      if (!e || e.target === prev) {
        prev.removeEventListener("transitionend", endTransition);
        prev.removeEventListener("animationend", endTransition);
        prev.classList.remove(...exitActiveClasses);
        prev.classList.remove(...exitToClasses);
        s1() === prev && set1(undefined);
        onAfterExit && onAfterExit(prev);
        if (props.mode === "outin") enterTransition(el, prev);
      }
    }
  }

  createComputed((prev) => {
    el = resolved();
    while (typeof el === "function") el = el();
    return untrack(() => {
      if (el && el !== prev) {
        if (props.mode !== "outin") enterTransition(el, prev);
        else if (first) set1(el);
      }
      if (prev && prev !== el && props.mode !== "inout")
        exitTransition(el, prev);
      first = false;
      return el;
    });
  });

  return [s1, s2];
};

const Fetchers = (function () {
  const Client = ({ accessToken, shopDomain, apiVersion }) => {
    const query = async ({ query, variables = {} }) => {
      return fetch(`https://${shopDomain}/api/${apiVersion}/graphql.json`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": accessToken,
          Accept: "application/json",
        },
        body: JSON.stringify({ query, variables }),
      }).then((res) => res.json());
    };

    return {
      query,
    };
  };

  const client = Client({
    ...window.Shopify.storefront,
  });

  const fetchProducts = async () =>
    client.query({
      query: `
      query products {
        products(first: 10) {
          nodes {
            id
            title
            variants(first: 10) {
              nodes {
                id
              }
            }
          }
        }
      }
    `,
    });

  const fetchBundleParent = async (id) =>
    client.query({
      query: `
      query bundleParent($id: ID!) {
        node(id: $id) {
          ...on ProductVariant {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            size: metafield(key: "size", namespace: "bundle") {
              value
            }
            children: metafield(key: "children", namespace: "bundle") {
              value
            }
          }
        }
      }
    `,
      variables: {
        id,
      },
    });

  const fetchBundleChildren = async (ids) =>
    client.query({
      query: `
      query bundleChildren($ids: [ID!]!) {
        nodes(ids: $ids) {
          ...on ProductVariant {
            id
            title
            quantityAvailable
            availableForSale
            price {
              amount
              currencyCode
            }
            image {
              altText
              url
            }
            product {
              tags
              title
              handle
              descriptionHtml
              fragranceDescription: metafield(namespace: "accentuate", key: "fragrance_description") {
                value
              }
              shipping: metafield(namespace: "accentuate", key: "shipping_returns") {
                value
              }
              reviewSnippet: metafield(namespace: "okendo", key: "ProductListingSnippet") {
                value
              }
              highlightsTitle: metafield(namespace: "details-review_highlight", key: "title") {
                value
              }
              highlightsContent: metafield(namespace: "details-review_highlight", key: "content") {
                value
              }
              highlightsRating: metafield(namespace: "details-review_highlight", key: "rating") {
                value
              }
              excerpt: metafield(namespace: "accentuate", key: "excerpt") {
                value
              }
              associatedCollection: metafield(namespace: "general", key: "associated_collection") {
                data: reference {
                  ... on Collection {
                    handle
                    title
                  }
                }
              }
              featuredImage {
                altText
                url
              }
            }
          }
        }
      }
    `,
      variables: {
        ids,
      },
    });

  return {
    fetchProducts,
    fetchBundleParent,
    fetchBundleChildren,
  };
})();

const GlobalState = (function (fetchers) {
  const initialState = {
    selectedType: document.querySelector('input[name="type"]:checked'),
    selectedSize: document.querySelector('input[name="size"]:checked'),
  };
  const limit = 24;

  const [type, setType] = createSignal(initialState.selectedType);
  const [size, setSize] = createSignal(initialState.selectedSize);
  const [page, setPage] = createSignal(0);
  const [bundleItems, setBundleItems] = createSignal([]);
  const [selectedVariant, setSelectedVariant] = createSignal(null);
  const [filter, setFilter] = createStore({
    color: "",
    search: "",
    sortBy: "",
    fragranceType: "",
    season: "",
  });

  const cache = new Map();

  const selectedVariantId = createMemo(
    on(size, (size) => {
      if (!size) return "";
      return formatResource(size.value, {
        resourceType: "ProductVariant",
      });
    })
  );

  const [parent] = createResource(
    selectedVariantId,
    fetchers.fetchBundleParent
  );

  const childrenIds = createMemo(() => {
    const children = parent()?.data?.node?.children?.value;
    if (!children || !children.length) return [];
    return JSON.parse(children);
  });

  const [variantsResource, { refetch: refetchVariants }] = createResource(
    childrenIds,
    fetchers.fetchBundleChildren
  );

  const checkSearchFilter = (variant) => {
    return (
      variant.title.toLowerCase().includes(filter.search.toLowerCase()) ||
      variant.product.title.toLowerCase().includes(filter.search.toLowerCase())
    );
  };

  const checkTagFilter = (variant, key, value) => {
    if (!value) return true;
    return variant.product.tags.includes(filter[key]);
  };

  // const cachedVariants = createMemo(() => {
  //   const curType = type().value;
  //   const id = quantity().getAttribute('data-additional-data');
  //   const key = `${curType}-${id}`;
  //   if (cache.has(key)) return cache.get(key);
  //   const variants = variantsResource()
  //   if (!variants || !variants?.data?.nodes.length) return [];
  //   cache.set(key, variants.data?.nodes);
  //   return cache.get(key);
  // })

  const filteredVariants = createMemo(() => {
    const variants = variantsResource()?.data?.nodes;
    if (!variants || !variants.length) return [];
    setPage(0);
    const filtered = variants.filter((variant) => {
      return (
        Boolean(variant) &&
        variant.availableForSale &&
        checkSearchFilter(variant) &&
        checkTagFilter(variant, "color", filter.color) &&
        checkTagFilter(variant, "fragranceType", filter.fragranceType) &&
        checkTagFilter(variant, "season", filter.season)
      );
    });
    const sorted = filtered.sort((a, b) => {
      const titleA = a.product.title.toLowerCase(),
        titleB = b.product.title.toLowerCase();
      switch (filter.sortBy) {
        case "low-high":
          return Number(a.price.amount) - Number(b.price.amount);
        case "high-low":
          return Number(b.price.amount) - Number(a.price.amount);
        case "a-z":
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        case "z-a":
          if (titleB < titleA) return -1;
          if (titleB > titleA) return 1;
          return 0;
      }
    });
    return sorted;
  });

  const paginatedVariants = createMemo(() => {
    const activePage = page();
    const variants = filteredVariants();
    return variants.slice(activePage * limit, (activePage + 1) * limit);
  });

  const isFull = createMemo(() => {
    const size = JSON.parse(parent()?.data?.node?.size?.value || "0"),
      items = bundleItems();
    return size === items.length;
  });

  return {
    parent,
    bundleSize: () => JSON.parse(parent()?.data?.node?.size?.value || "0"),
    isFull,
    isFetching: () => variantsResource.loading,
    includedVariants: paginatedVariants,
    getTotalVariantCount: () => filteredVariants().length,
    pageInfo: [page, setPage],
    variants: [variantsResource, refetchVariants],
    selectedVariant: [selectedVariant, setSelectedVariant],
    bundleItems: [
      bundleItems,
      {
        addItemToBundle: (variant) => {
          if (isFull()) return true;
          setBundleItems([...bundleItems(), variant]);
        },
        setBundleItems: (items) => setBundleItems(items),
        removeItemFromBundle: (index) => {
          setBundleItems((items) => {
            return items.slice(0, index).concat(items.slice(index + 1));
          });
        },
        clearBundleItems: () => setBundleItems([]),
      },
    ],
    size: [size, setSize],
    type: [type, setType],
    filter: [
      filter,
      {
        setColor: (color) => setFilter("color", color),
        setFragranceType: (fragranceType) =>
          setFilter("fragranceType", fragranceType),
        setSeason: (season) => setFilter("season", season),
        setSearch: (search) => setFilter("search", search),
        setSortBy: (sortBy) => setFilter("sortBy", sortBy),
      },
    ],
    limit,
  };
})(Fetchers);

const IncludedProducts = (function (state) {
  const VariantItemAction = (variant) => {
    const [showQuantity, setShowQuantity] = createSignal(false);
    const [isInteracting, setIsInteracting] = createSignal(false);

    const [bundleItems, { addItemToBundle, removeItemFromBundle }] =
      state.bundleItems;

    const items = createMemo(() =>
      bundleItems()
        .map((item, index) => ({ ...item, index }))
        .filter((b) => variant.id === b.id)
    );

    const remaining = createMemo(() => {
      return variant.quantityAvailable - items().length;
    });

    let addRef = null;

    const onClick = (e) => {
      e.preventDefault();

      if (!items().length && remaining() >= 1)
        return addItemToBundle({ ...variant });
      return setShowQuantity(true);
    };

    onMount(() => {
      addRef.addEventListener("click", onClick);
    });

    onCleanup(() => {
      addRef.removeEventListener("click", onClick);
    });

    createEffect(() => {
      const isShowing = showQuantity();
      const currentlyInteracting = isInteracting();
      if (!isShowing || currentlyInteracting) return;
      const id = setTimeout(() => {
        setShowQuantity(false);
      }, 2000);

      return onCleanup(() => {
        clearTimeout(id);
      });
    });

    const isDisabled = () => {
      return !variant.availableForSale || (!items().length && state.isFull());
    };

    return html`
      <button
        ref=${(el) => (addRef = el)}
        id=${`candleId-${variant.id}`}
        class="btn btn-primary prodATC position-relative"
        disabled=${isDisabled}
        style=${() =>
          isDisabled() ? { display: "none" } : { display: "inline-block" }}
      >
        ${() => {
          if (items().length)
            return html`
              <p style="margin-bottom: 0;">
                ${items().length} <small style="font-size: .75em;">x</small>
              </p>
            `;
          return html`<i class="fa fa-plus"></i>`;
        }}
      </button>
      <${Transition}
        name="fadeScale"
        ...${animations.fadeInScale({ y: { enter: "-50%", exit: "-50%" } })}
      >
        ${() => showQuantity() && html`<${ShowQuantity} />`}
      <//>
    `;

    function ShowQuantity() {
      let ref = null;

      onMount(() => {
        function handleInteractionStart() {
          setIsInteracting(true);
        }

        function handleInteractionEnd() {
          setIsInteracting(false);
        }

        let id = null;

        function handleClick() {
          if (id) clearTimeout(id);
          return setTimeout(() => {
            setIsInteracting(false);
          }, 2000);
        }

        ref.addEventListener("click", handleClick);
        ref.addEventListener("mouseenter", handleInteractionStart);
        ref.addEventListener("mouseleave", handleInteractionEnd);

        onCleanup(() => {
          ref.removeEventListener("click", handleClick);
          ref.removeEventListener("mouseenter", handleInteractionStart);
          ref.removeEventListener("mouseleave", handleInteractionEnd);
        });
      });

      const buttonStyle = {
        border: "0px solid",
        width: "max-content",
        height: "max-content",
        "background-color": "transparent",
        color: "white",
      };

      return html`
        <div
          class="btn btn-primary position-absolute d-flex"
          ref=${(el) => (ref = el)}
          style=${{
            top: "50%",
            left: "50%",
            "z-index": 2,
            gap: "1rem",
            width: "max-content",
            "--translate-x": "-50%",
            "border-radius": "1000rem",
          }}
        >
          <button
            style=${buttonStyle}
            onClick=${(e) => {
              e.preventDefault();
              if (items().length === 1) {
                setIsInteracting(false);
                setShowQuantity(false);
              }
              removeItemFromBundle(items()[0].index);
            }}
          >
            ${() =>
              items().length > 1
                ? html`<i class="fa fa-minus"></i>`
                : html`<i class="fa fa-trash-o"></i>`}
          </button>
          <p style="margin-bottom: 0;">${() => items().length}</p>
          <button
            style=${() => ({
              ...buttonStyle,
              opacity: state.isFull() ? 0.5 : 1,
            })}
            disabled=${() => state.isFull()}
            onClick=${(e) => {
              e.preventDefault();
              if (state.isFull()) return;
              addItemToBundle({ ...variant });
            }}
          >
            <i class="fa fa-plus"></i>
          </button>
        </div>
      `;
    }
  };

  const VariantItemSkeleton = () => {
    return html`
      <div class="col-6 col-sm-4">
        <div style="width: 100%; aspect-ratio:1;">
          <div class="skeleton-box" />
        </div>
      </div>
    `;
  };

  const VariantItem = (item) => {
    const [_, setSelectedVariant] = state.selectedVariant;
    const image = item?.image || item?.product?.featuredImage;

    return html`
      <div class="col-6 col-sm-4">
        <div class="prodGridItem">
          <a
            role="button"
            data-bs-toggle="modal"
            data-bs-target="#bundleModal"
            onClick=${(e) => setSelectedVariant({ ...item })}
            aria-label=${`View more details of ${item.product.title}`}
          >
            <img
              loading="lazy"
              width="306"
              height="306"
              src="${image.url}"
              alt=" ${image.altText}"
            />
          </a>
          <p class="prodGridTitle">
            <a
              role="button"
              data-bs-toggle="modal"
              data-bs-target="#bundleModal"
              onClick=${(e) => setSelectedVariant({ ...item })}
              aria-label=${`View more details of ${item.product.title}`}
            >
              ${item.product.title}
            </a>
          </p>
          <div
            class="prodGridCTA d-flex align-items-center justify-content-center"
          >
            <${VariantItemAction} ...${item} />
          </div>
        </div>
      </div>
    `;
  };

  const Entry = () => {
    return html`
      <${Switch}>
        <${Match} when=${() => state.isFetching()}>
          <${For} each=${Array(state.limit).fill(1)}>
            ${() => html`<${VariantItemSkeleton} />`}
          <//>
        <//>
        <${Match} when=${() => state.includedVariants()}>
          <${For}
            each=${state.includedVariants}
            fallback=${html`<h3>No items match your filters</h3>`}
          >
            ${(variant) => html`
              <${Show} when=${variant}>
                <${VariantItem} ...${variant} />
              <//>
            `}
          <//>
        <//>
      <//>
    `;
  };

  render(Entry, document.querySelector(".bundleProductList"));
})(GlobalState);

const Pagination = (function (state) {
  const PaginationButton = (props) => {
    const [activePage, setActivePage] = state.pageInfo;
    const className = createMemo(() =>
      activePage() === props.page ? "page-number active" : "page-number"
    );
    return html`
      <li class=${className} onClick=${(e) => setActivePage(props.page)}>
        ${props.page + 1}
      </li>
    `;
  };

  const Entry = () => {
    const pages = createMemo(() => {
      return Array(Math.ceil(state.getTotalVariantCount() / state.limit))
        .fill(0)
        .map((_, i) => i);
    });

    return html`
      <${Show} when=${() => pages().length > 1}>
        <ul>
          <${For} each=${pages}>
            ${(page) => html`<${PaginationButton} page=${page} />`}
          <//>
        </ul>
      <//>
    `;
  };

  render(Entry, document.querySelector(".pagination"));
})(GlobalState);

const Filters = (function (state) {
  const filterBody = document.querySelector(".bundleFilters");

  const [_, { setFragranceType, setColor, setSeason, setSearch, setSortBy }] =
    state.filter;

  filterBody.addEventListener("input", (e) => {
    switch (e.target.name) {
      case "Color":
        return setColor(e.target.value);
      case "Fragrance":
        return setFragranceType(e.target.value);
      case "Season":
        return setSeason(e.target.value);
      case "SortBy":
        return setSortBy(e.target.value);
      case "bundleSearch":
        return setSearch(e.target.value);
    }
  });
})(GlobalState);

const BundleChange = (function (state) {
  const [type, setType] = state.type;
  const [_, setSize] = state.size;
  const [__, { clearBundleItems, setBundleItems }] = state.bundleItems;
  const [variantsRequest, refetch] = state.variants;

  const updater = (cb, duration = 200) => {
    let timeout = null;
    let updates = [];
    return (...args) => {
      if (timeout) clearTimeout(timeout);
      updates.push(cb(...args));
      timeout = setTimeout(() => {
        batch(() => {
          updates.forEach((update) => {
            update();
            updates.shift();
          });
        });
        updates = [];
      }, duration);
    };
  };

  const trackVariants = createReaction(() => {
    const variants =
      variantsRequest()?.data?.nodes?.filter((variant) => variant) || [];
    const input = type();

    if (input.getAttribute("data-type") !== "prebuilt") return;
    if (!variants.length) return;

    const parent = input.parentNode;
    const included = parent.querySelector("input[name='included'");
    const preselectedIds = JSON.parse(included.value).map((id) =>
      formatResource(id, { resourceType: "ProductVariant" })
    );

    const preselectedVariants = variants.filter((variant) =>
      preselectedIds.includes(variant.id)
    );

    setBundleItems(preselectedVariants);
  });

  const trackType = createReaction(() => {
    refetch();
    trackVariants(() => variantsRequest());
  });

  const handleUpdate = function (e) {
    if (e.target.tagName !== "INPUT") return;
    if (e.target.type !== "radio") return;
    const target = e.target;
    switch (target.name) {
      case "type": {
        clearBundleItems();
        trackType(() => type());
        return setType(target);
      }
      case "size": {
        clearBundleItems();
        return setSize(target);
      }
      default:
        return null;
    }
  };

  createEffect(
    on(type, (input) => {
      const title = input.getAttribute("data-title");
      const titleSlots = Array.from(
        document.querySelectorAll("[data-bundle-title]")
      );
      if (!title || !titleSlots.length) return null;
      titleSlots.forEach((el) => (el.textContent = title));
    })
  );

  addEventListener("change", handleUpdate);
})(GlobalState);

const BundleView = (function (state) {
  const Placeholder = (props) => {
    return html`
      <div class="col-4">
        <div class="bundleItem">
          <div class="circleItem"><i class="fa fa-plus"></i></div>
        </div>
      </div>
    `;
  };

  const BundleItem = (item) => {
    const [_, { removeItemFromBundle }] = state.bundleItems;
    const [__, setSelectedVariant] = state.selectedVariant;
    const image = item?.image ||
      item?.product?.featuredImage || {
        url: Shopify.icons["bundle-placeholder"],
        altText: "Placeholder image for a candle",
      };
    const title = `${item.product.title}`;
    return html`
      <div class="col-4">
        <div class="bundleItem chosen" data-variant="${item.id}">
          <button
            class="bundleItemRemove p-3 d-flex align-items-center justify-content-center position-absolute top-0 end-0"
            aria-label=${`Remove ${item.product.title} from bundle`}
            id=${`remove-${item.id}`}
            onClick=${(e) => removeItemFromBundle(item.index)}
          >
            <i class="fa fa-times"></i>
          </button>
          <a
            role="button"
            data-bs-target="#bundleModal"
            data-bs-toggle="modal"
            class="circleItem"
            onClick=${(e) => setSelectedVariant({ ...item })}
          >
            <img
              loading="lazy"
              width="180"
              height="180"
              src=${image.url}
              alt=${image.altText}
            />
          </a>
        </div>
      </div>
    `;
  };

  const Entry = () => {
    const [bundleItems, _] = state.bundleItems;
    const placeholders = createMemo(() =>
      Array(
        JSON.parse(state.parent()?.data?.node?.size?.value || 0) -
          bundleItems().length
      ).fill(1)
    );

    const items = createMemo(() => {
      const bItems = bundleItems();
      return [...bItems, ...placeholders()];
    });

    return html`
      <div class="row bundleFirstSet d-flex justify-content-center gy-4">
        <${For} each=${items}>
          ${(item, i) => html`
            <${Switch}>
              <${Match} when=${() => typeof item === "number"}>
                <${Placeholder} index=${() => i() + 1} />
              <//>
              <${Match} when=${() => typeof item === "object"}>
                <${BundleItem} index=${i} ...${item} />
              <//>
            <//>
          `}
        <//>
      </div>
    `;
  };

  const bundle = document.getElementById("bundle");
  bundle.replaceChildren();
  render(Entry, bundle);
})(GlobalState);

const BundleModal = (function (state) {
  const Entry = () => {
    const [selectedVariant, _] = state.selectedVariant;
    const image = () =>
      selectedVariant()?.image ||
      selectedVariant()?.product?.featuredImage || {
        url: Shopify.icons["bundle-placeholder"],
        altText: "Placeholder image for a candle",
      };

    const Photo = (props) => {
      return html`
        <div id="pdpPhotos">
          <img src=${props.url} alt=${props.altText} id="ProductPhotoImg" />
        </div>
      `;
    };

    const HighlightItem = (highlight) => {
      const ratingWidth = (parseFloat(highlight.rating) / 5) * 100;
      return html`
        <li class="highlight__item">
          <div class="highlight__item--rating">
            <div class="oke-review">
              <div class="okeReviews okeReviews--theme">
                <div class="okeReviews-reviewsSummary">
                  <div class="okeReviews-reviewsSummary-starRating">
                    <span
                      class="okeReviews-starRating okeReviews-starRating--small"
                    >
                      <span
                        class="okeReviews-starRating-indicator"
                        role="presentation"
                      >
                        <span
                          class="okeReviews-starRating-indicator-layer"
                        ></span>
                        <span
                          class="okeReviews-starRating-indicator-layer okeReviews-starRating-indicator-layer--foreground"
                          style=${`width: ${ratingWidth}%;`}
                        ></span>
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <h3 class="highlight__item--title h5">${highlight.title}</h3>
          <div
            class="highlight__item--content prose"
            innerHTML=${highlight.content}
          />
        </li>
      `;
    };

    const Accordion = (variant) => {
      const highlightsContent = JSON.parse(
        variant?.product?.highlightsContent?.value || "[]"
      );
      const highlightsTitle = JSON.parse(
        variant?.product?.highlightsTitle?.value || "[]"
      );
      const highlightsRating = JSON.parse(
        variant?.product?.highlightsRating?.value || "[]"
      );
      const shipping = JSON.parse(variant?.product?.shipping?.value || "{}");
      const fragranceDescription = JSON.parse(
        variant?.product?.fragranceDescription?.value || "{}"
      );

      return html`
        <div class="accordion" id="detailsAccordion">
          <${Show}
            when=${highlightsContent.length &&
            highlightsTitle.length &&
            highlightsRating.length}
          >
            <div class="accordion-item">
              <h2 class="accordion-header" id="reviewHighlights">
                <button
                  class="accordion-button collapse show"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#highlightsCollapse"
                  aria-expanded="false"
                  aria-controls="highlightsCollapse"
                >
                  Review Highlights
                </button>
              </h2>
              <div
                id="highlightsCollapse"
                class="accordion-collapse show"
                aria-labelledby="reviewHighlights"
                data-bs-parent="#detailsAccordion"
              >
                <div class="accordion-body">
                  <ul class="highlights">
                    <${For} each=${highlightsContent}>
                      ${(highlight, i) => html`
                        <${HighlightItem}
                          ...${{
                            content: highlight.html,
                            title: highlightsTitle[i()],
                            rating: highlightsRating[i()],
                          }}
                        />
                      `}
                    <//>
                  </ul>
                </div>
              </div>
            </div>
          <//>
          <${Show} when=${fragranceDescription?.html}>
            <div class="accordion-item">
              <h2 class="accordion-header" id="fragranceDescription">
                <button
                  class="accordion-button"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#fragranceCollapse"
                  aria-expanded="true"
                  aria-controls="fragranceCollapse"
                >
                  Fragrance
                </button>
              </h2>
              <div
                id="fragranceCollapse"
                class="accordion-collapse collapse show"
                aria-labelledby="fragranceDescription"
                data-bs-parent="#detailsAccordion"
              >
                <div class="accordion-body">
                  <div innerHTML=${fragranceDescription?.html} />
                </div>
              </div>
            </div>
          <//>
          <div class="accordion-item">
            <h2 class="accordion-header" id="productDetails">
              <button
                class="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#detailsCollapse"
                aria-expanded="false"
                aria-controls="detailsCollapse"
              >
                Product Details
              </button>
            </h2>
            <div
              id="detailsCollapse"
              class="accordion-collapse collapse"
              aria-labelledby="productDetails"
              data-bs-parent="#detailsAccordion"
            >
              <div class="accordion-body">
                <div innerHTML=${variant?.product?.descriptionHtml} />
              </div>
            </div>
          </div>
          <${Show} when=${shipping?.html}>
            <div class="accordion-item">
              <h2 class="accordion-header" id="shippingReturns">
                <button
                  class="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#returnsCollapse"
                  aria-expanded="false"
                  aria-controls="returnsCollapse"
                >
                  Shipping & Returns
                </button>
              </h2>
              <div
                id="returnsCollapse"
                class="accordion-collapse collapse"
                aria-labelledby="shippingReturns"
                data-bs-parent="#detailsAccordion"
              >
                <div class="accordion-body">
                  <div innerHTML=${shipping?.html} />
                </div>
              </div>
            </div>
          <//>
        </div>
      `;
    };

    const PdpDetails = (variant) => {
      const [bundleItems, { addItemToBundle }] = state.bundleItems;
      const product = variant?.product;
      const associatedCollection = variant?.product?.associatedCollection?.data;
      const reviewSnippet = variant?.product?.reviewSnippet?.value;

      const remaining = createMemo(() => {
        const quantity = bundleItems().filter(
          (v) => v.id === variant.id
        ).length;
        return variant.quantityAvailable - quantity;
      });

      const onClick = (e) => {
        e.preventDefault();
        const button = e.currentTarget;
        const ogText = button.textContent;
        button.setAttribute("disabled", true);
        button.textContent = "Adding...";

        const isFull = addItemToBundle({ ...variant });

        if (remaining() < 1) {
          return setTimeout(() => {
            button.textContent = "Added to bundle";
            setTimeout(() => {
              button.textContent = "Out of stock";
            }, 1000);
          }, 500);
        }

        if (isFull) {
          return setTimeout(() => {
            button.textContent = "Bundle full";
            setTimeout(() => {
              button.textContent = ogText;
              button.removeAttribute("disabled");
            }, 1000);
          }, 500);
        }
        setTimeout(() => {
          button.textContent = "Added to bundle";
          setTimeout(() => {
            button.textContent = ogText;
            button.removeAttribute("disabled");
          }, 1000);
        }, 500);
      };

      return html`
        <div id="pdpDetails" class="bundleQuick">
          <${Show} when=${() => associatedCollection}>
            <p class="prodCollection mb-0">
              <a href=${() => `/collections/${associatedCollection.handle}`}>
                ${() => associatedCollection.title}
              </a>
            </p>
          <//>
          <h3>${product?.title}</h3>
          <${Show} when=${() => reviewSnippet}>
            <div innerHTML=${() => reviewSnippet} />
          <//>
          <div class="prodOptions">
            <${Show} when=${() => product?.excerpt?.value}>
              <div class="prodExcerpt mt-3">
                <p>${() => product?.excerpt?.value}</p>
              </div>
            <//>
            <div class="variantTitles">
              <p>
                ${() => variant?.title}
                ${() => (variant?.availableForSale ? `` : " - Sold out")}
              </p>
            </div>
          </div>

          <div class="prodAddToCart mb-4 col-sm-6">
            <button
              type="button"
              name="add"
              class="prodATC btn btn-primary mb-2"
              disabled=${() => !variant?.availableForSale}
              onClick=${(e) => (variant?.availableForSale ? onClick(e) : null)}
            >
              Add to Bundle
            </button>
          </div>
          <${Accordion} ...${variant} />
        </div>
      `;
    };

    return html`
      <div
        class="modal fade"
        id="bundleModal"
        tabindex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header border-0">
              <button
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div class="modal-body">
              ${() =>
                selectedVariant() &&
                html`
                  <section id="pdpMain" class="py-0">
                    <div class="container">
                      <div class="row">
                        <div class="col-sm-6">
                          <${Photo}
                            ...${selectedVariant()?.image ||
                            selectedVariant()?.product?.featuredImage}
                          />
                        </div>
                        <div class="col-sm-6">
                          <${PdpDetails} ...${selectedVariant()} />
                        </div>
                      </div>
                    </div>
                  </section>
                `}
            </div>
          </div>
        </div>
      </div>
    `;
  };

  render(Entry, document.body);
})(GlobalState);

var generateToken = function () {
  var result = "";
  var characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < 20; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

const AddBundleToCart = (function (state) {
  const button = document.querySelector("button.addAllToCart");
  if (!button) return;

  const onBundleFull = () => {
    button.removeAttribute("disabled");
  };

  const onBundleEmpty = () => {
    button.setAttribute("disabled", "disabled");
  };

  const onBundleAdding = (button) => {
    button.setAttribute("disabled", "disabled");
    button.classList.add("disabled");
    button.textContent = "Adding...";
  };

  function updateButtonAdding(cb) {
    window.setTimeout(function () {
      button.classList.remove("disabled");
      button.removeAttribute("disabled");
      button.textContent = "Add Bundle To Cart";

      if (!cb) return;
      cb();
    }, 1000);
  }

  const createOnClick = (cb) => (e) => {
    if (!cb) return;
    if (
      !e.target.classList.contains("addAllToCart") ||
      !e.target.closest("button.addAllToCart")
    )
      return;
    e.preventDefault();

    return cb(e);
  };

  const handleOnClick = (e) => {
    const [bundleItems, { clearBundleItems }] = state.bundleItems;
    const parent = state.parent()?.data?.node;
    const onClick = (e) => {
      e.preventDefault();
      const bundleSku = generateToken();
      const variantsAttribute = {
        key: "_variants",
        value: [],
      };
      const rest = bundleItems().map((item, i) => {
        variantsAttribute.value.push(parseResourceId(item.id));
        return {
          key: `${i + 1}`,
          value: `${item.product.title}`,
        };
      });

      variantsAttribute.value = variantsAttribute.value.join(",");

      const item = {
        merchandiseId: parseInt(parseResourceId(parent.id)),
        quantity: 1,
        attributes: [
          {
            key: "_bundleSku",
            value: bundleSku,
          },
          {
            key: "_size",
            value: `${state.bundleSize()}`,
          },
          variantsAttribute,
          ...rest,
        ],
      };

      onBundleAdding(e.target);

      Shopify.AjaxifyCart.addItem(item).then(function () {
        updateButtonAdding(() => {
          Shopify.CartDrawer.open();
          return clearBundleItems();
        });
      });
    };

    const handleClick = createOnClick(onClick);

    addEventListener("click", handleClick);

    return () => removeEventListener("click", handleClick);
  };

  createEffect(() => {
    const bundleIsFull = state.isFull();
    if (bundleIsFull) return onBundleFull();
    return onBundleEmpty();
  });

  createEffect(
    on(state.parent, (parent) => {
      const unsubscribe = handleOnClick();
      return onCleanup(() => {
        unsubscribe();
      });
    })
  );
})(GlobalState);

// const ScrolledCTA = (function (state) {
//   const bundle = document.getElementById("bundle");
//   if (!bundle) return;

//   const [isVisible, setIsVisible] = createSignal(true);

//   const debounce = (cb, duration = 100) => {
//     let timeout = null;
//     return (...args) => {
//       if (timeout) clearTimeout(timeout);
//       timeout = setTimeout(() => {
//         cb(...args);
//       }, duration);
//     };
//   };

//   const callback = debounce((entries) => {
//     const isVisible = entries.reduce(
//       (isIntersecting, entry) => isIntersecting || entry.isIntersecting,
//       false
//     );
//     setIsVisible(isVisible);
//   });

//   const observer = new IntersectionObserver(callback, {
//     rootMargin: "0px 0px 100px 0px",
//   });
//   observer.observe(bundle);

//   const Entry = () => {
//     return html`
//       <${Transition} name="slide" ...${animations.slideFromBottom}>
//         ${() =>
//           !isVisible() &&
//           html`
//           <nav id="btbBundleNav">
//             <a id="btbBundleGoto" class="btn btn-primary" role="button" onClick=${(
//               e
//             ) =>
//               bundle.scrollIntoView({
//                 block: "center",
//                 inline: "center",
//               })}>Back to Bundle <i class="fas fa-level-up-alt"></i></button>
//           </nav>
//         `}
//       <//>
//     `;
//   };

//   render(Entry, document.body);
// })(GlobalState);

const BundlePrices = (function (state) {
  const price = document.querySelector("[data-bundle-price]");
  if (!price) return;

  const compareAtPrice = document.querySelector("[data-bundle-save]");
  const getVariant = () => state.parent()?.data?.node;

  const formatPrice = (money) =>
    new Intl.NumberFormat("en-US", {
      currency: getVariant().price.currencyCode,
      style: "currency",
    }).format(money);

  createEffect(() => {
    const variant = getVariant();
    if (!variant) return;
    const vPrice = parseFloat(variant.price?.amount);
    const cPrice = parseFloat(variant.compareAtPrice?.amount);
    price.textContent = formatPrice(vPrice);

    if (cPrice > vPrice) {
      compareAtPrice.textContent = formatPrice(cPrice - vPrice);
    }
  });
})(GlobalState);

const BundleDash = (function (state) {
  const bundle = document.getElementById("bundle");
  if (!bundle) return;

  const [isVisible, setIsVisible] = createSignal(true);

  const getVariant = () => state.parent()?.data?.node;

  const formatPrice = (money) =>
    new Intl.NumberFormat("en-US", {
      currency: getVariant().price.currencyCode,
      style: "currency",
    }).format(money);

  inView(bundle, () => {
    setIsVisible(false);
    return () => setIsVisible(true);
  });

  const BundleItem = (item) => {
    const [_, { removeItemFromBundle }] = state.bundleItems;

    const image = item?.image ||
      item?.product?.featuredImage || {
        url: Shopify.icons["bundle-placeholder"],
        altText: "Placeholder image for a candle",
      };

    let ref = null;
    let tooltip = null;

    onMount(() => {
      tooltip = new bootstrap.Tooltip(ref, {
        trigger: "hover focus",
        placement: "top",
        fallbackPlacements: ["top"],
      });

      onCleanup(() => {
        tooltip.dispose();
      });
    });

    return html`
      <div
        class="bundleDashItem position-relative"
        title=${item.product.title}
        data-variant="${item.id}"
        ref=${(el) => (ref = el)}
        onMouseOver=${() => (tooltip ? tooltip.show() : null)}
        onMouseOut=${() => (tooltip ? tooltip.hide() : null)}
      >
        <button
          class="bundleItemRemove d-flex align-items-center justify-content-center top-0 end-0 z-2 position-absolute"
          id=${`remove-${item.id}`}
          aria-label=${`Remove ${item.product.title} from bundle`}
          onClick=${(e) => removeItemFromBundle(item.index)}
        >
          <i class="fa fa-times"></i>
        </button>
        <img
          width="75"
          height="75"
          src=${image.url}
          alt=${image.altText}
          class="mx-auto bundleDashCandle"
        />
      </div>
    `;
  };

  const Placeholder = (props) => {
    return html`
      <div class="bundleDashItem position-relative">
        <img
          src=${Shopify.icons["bundle-placeholder"]}
          alt="Placeholder image for a candle"
          loading="lazy"
          width="75"
          height="75"
          class="mx-auto bundleDashCandle"
          style="max-height: 75px;"
        />
        <p
          class="position-absolute"
          style="top: 50%; left: 50%; transform: translate(-50%, -50%); text-center;"
        >
          <strong>${props.index}</strong>
        </p>
      </div>
    `;
  };

  const Dash = (props) => {
    const [bundleItems, _] = state.bundleItems;
    const [lastIndex, setLastIndex] = createSignal(0);

    const placeholders = createMemo(() =>
      Array(
        JSON.parse(state.parent()?.data?.node?.size?.value || 0) -
          bundleItems().length
      ).fill(1)
    );

    const items = createMemo(() => {
      const bItems = bundleItems();
      return [...bItems, ...placeholders()];
    });

    let ref = null;

    createEffect(
      on(bundleItems, (items) => {
        if (!ref) return;
        const slider = new KeenSlider(ref, {
          slides: {
            perView: "auto",
          },
        });
        slider.update(undefined, lastIndex());
        slider.moveToIdx(items.length - 1);

        return onCleanup(() => {
          setLastIndex(slider.track.details.abs);
          slider.destroy();
        });
      })
    );

    return html`
      <div class="bundleDashContainer container d-flex">
        <div class="keen-slider" ref=${(el) => (ref = el)}>
          <${For} each=${items}>
            ${(item, i) => html`
              <div
                class="keen-slider__slide"
                style="min-width: max-content; max-width: max-content;"
              >
                <${Switch}>
                  <${Match} when=${() => typeof item === "number"}>
                    <${Placeholder} index=${() => i() + 1} />
                  <//>
                  <${Match} when=${() => typeof item === "object"}>
                    <${BundleItem} index=${i} ...${item} />
                  <//>
                <//>
              </div>
            `}
          <//>
        </div>
        <div
          class="flex-col"
          style="min-width: 125px; position: relative; background-color: white; padding: var(--children-spacing);"
        >
          <div
            class="d-flex align-items-center justify-content-center h-100 w-100"
          >
            <button
              class="bundleCTA btn btn-primary addAllToCart w-100"
              disabled=${() => !state.isFull()}
            >
              Add to Cart&nbsp;
              <br class="d-block d-lg-none" />
              <span data-bundle-price class="d-block d-md-inline">
                ${formatPrice(getVariant()?.price?.amount)}
              </span>
            </button>
          </div>
        </div>
      </div>
    `;
  };

  const Entry = () => {
    const [bundleItems, _] = state.bundleItems;
    const hasBundleItems = createMemo(() => bundleItems().length > 0);

    return html`
      <${Transition} name="slide" ...${animations.slideFromBottom}>
        ${() =>
          isVisible() &&
          hasBundleItems() &&
          html`<section
            id="bundleDash"
            class="d-flex align-items-center justify-content-center"
          >
            <${Dash} />
          </section> `}
      <//>
    `;
  };
  render(Entry, document.body);
})(GlobalState);
